import { DatePipe } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { NavigationEnd } from '@angular/router';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { filter } from 'rxjs';
import { HandleLoadingState } from '../../../decorators/handle-loading-state.decorator';
import { DateEx, WEEKDAYS } from '../../../functions/date-ex';
import { leaveLink } from '../../../functions/claim/leaveLink';
import { numericAdaOrLeaveKeyFromClaim } from '../../../functions/todo-claim-numeric-key';
import { DiaryCountResponse } from '../../../models/api/todo/client-diaries/diary-count-response';
import { SearchDiariesRequest } from '../../../models/api/todo/client-diaries/search-diaries-request';
import { Action } from '../../../models/core/action';
import { FormatPipe } from '../../../pipes/format.pipe';
import { ClientDiaryDataService } from '../../../services/todo/client-diary-data.service';
import { BaseComponent } from '../../base/base.component';
import { RadioButtonComponent } from '../../core/controls/radio-button.component';
import { LoadingState } from '../../core/panels/loading-panel.component';
import { PanelComponent } from '../../core/panels/panel.component';
import { DiarySearchComponent } from './diary-search.component';
import { DiaryTableTopComponent } from './diary-table-top.component';
import { DiaryWeekComponent } from './diary-week.component';

@Component({
  selector: 'app-diary-view',
  imports: [
    DiaryTableTopComponent,
    DiaryWeekComponent,
    ReactiveFormsModule,
    PanelComponent,
    DiarySearchComponent,
    RadioButtonComponent,
  ],
  providers: [DatePipe, FormatPipe],
  template: `
    <div class="">
      <app-radio-button
        [control]="tab"
        label="Diary view"
        optionTitle="title"
        optionLabel="label"
        optionValue="key"
        [options]="tabs"
        [vertical]="false"
        (change)="onSelectRadio($event)"
      ></app-radio-button>
    </div>
    <div class="tw-py-6">
      <div class="tw-py-6" [hidden]="tab.value !== 'browse'">
        <app-diary-week
          [items]="filteredCalendarItems"
          [claim]="claim"
          [loading]="tableLoading && !isClndrClicked"
          [isPageTodo]="isPageTodo"
          (includeCompletedChange)="onIncludeCompletedChange($event)"
          (calendarAction)="onCalendarAction($event)"
        />
      </div>
      <div class="tw-py-6" [hidden]="tab.value !== 'search'">
        <app-diary-search
          [isPageTodo]="isPageTodo"
          (searchRequest)="searchRequest($event)"
          (clear)="onClearSearch()"
        />
      </div>
    </div>
    <h2 class="tw-text-base tw-flex tw-w-full tw-min-w-0 tw-break-words">
      {{ activeTitle }}
    </h2>
    <app-panel>
      <app-diary-table-top
        [diaryEntries]="filteredDiaryData"
        [isPageTodo]="isPageTodo"
        [loading]="tableLoading"
        [error]="error"
        [showSelection]="false"
        (searchCountEvent)="searchCountEvent($event)"
        (markCompleteEvent)="markCompleteEvent($event)"
      />
    </app-panel>
  `,
  styles: ``,
})
export class DiaryViewComponent extends BaseComponent implements OnInit {
  private clientDiaryData = inject(ClientDiaryDataService);
  private formatPipe = inject(FormatPipe);

  @Input()
  loading = false;

  @Output()
  search = new EventEmitter();

  @Output()
  clear = new EventEmitter();

  activeTitle: string;
  selectedDate: string;
  searchCount: number;
  diaryCount: DiaryCountResponse;
  claim: any = null;
  pastDue: boolean;
  initializingDiaries: boolean;
  diaryResponseData: any;
  diaryData: any = [];

  diaryRequest: SearchDiariesRequest;

  protected tab = new FormControl<string>('browse');

  protected state: LoadingState = 'Loading';
  tableLoading = false;
  error: any = null;

  protected tabs: Action[] = [
    { label: 'Browse', key: 'browse', title: 'Browse Shared Diaries' },
    { label: 'Search', key: 'search', title: 'Search Diaries' },
  ];

  weekStartDate: any;
  weekEndDate: any;
  formattedWeekRange: string;
  includeCompleted = false;
  calendarItems: any[] = [];

  isPageTodo = false;
  isClndrClicked = false;
  isMarkCompleteEventFired = false;

  private weekDiaryData: any[] = [];
  private selectedDayData: any[] = [];

  private _lastDiaryData: any[] = [];
  private _lastIncludeCompleted = false;
  private _lastIsPageTodo = false;
  private _lastFilteredDiaryTableData: any[] = [];

  private _lastCalendarItems: any[] = [];
  private _lastIncludeCompletedCal = false;
  private _lastSelectedDate = '';
  private _lastFilteredCalendarItems: any[] = [];

  constructor() {
    super();
    let previousUrl = this.router.url ?? '';
    this.dispose.subs = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(async (event) => {
        if (previousUrl.startsWith('/diary-edit')) {
          await this.getDiaryEntries();
        }
        previousUrl = event.urlAfterRedirects ?? event.url;
      });
  }

  async ngOnInit() {
    this.isPageTodo = document.location.pathname === '/todo';
    this.claim = this.view.currentClaim();
  }

  /** Diary `RECORD_ID` for claim URLs (same idea as task `navigatingRecID`). */
  private normalizeDiaryRecordId(diary: any): string | number | null {
    const rid = diary?.recordID ?? diary?.recordId;
    if (rid == null || rid === '') {
      return null;
    }
    if (typeof rid === 'object' && rid !== null && 'value' in rid) {
      const v = (rid as { value: unknown }).value;
      return v as string | number;
    }
    return rid as string | number;
  }

  private getRecordSourceAndId(diary: any): {
    recordSource: string;
    recordId: any;
  } {
    if (diary.claimUID) {
      return { recordSource: 'J', recordId: diary.claimUID };
    }
    const ln = diary?.leaveNumber;
    if (ln !== undefined && ln !== null && ln !== '') {
      return { recordSource: 'L', recordId: ln };
    }
    if (diary.absenceUID) {
      return {
        recordSource: 'T',
        recordId:
          diary.absenceUID ||
          diary.employeeUID ||
          this.normalizeDiaryRecordId(diary) ||
          this.claim?.recordId,
      };
    }
    const tamsReq =
      diary?.adatamsRequestID ?? diary?.adaTamsRequestID ?? null;
    if (tamsReq) {
      return { recordSource: 'R', recordId: tamsReq };
    }
    if (diary.adaEventNumber) {
      return { recordSource: 'A', recordId: diary.adaEventNumber };
    }
    if (diary.employeeUID) {
      return {
        recordSource: 'T',
        recordId:
          diary.employeeUID ||
          this.normalizeDiaryRecordId(diary) ||
          this.claim?.recordId,
      };
    }
    return { recordSource: '', recordId: null };
  }

  private matchesCurrentClaim(
    diary: any,
    recordSource: string,
    recordId: any,
  ): boolean {
    if (!this.claim || !this.claim.recordSource) {
      return false;
    }

    if (leaveLink(this.claim.recordSource) && recordSource === 'L') {
      const claimLeave = numericAdaOrLeaveKeyFromClaim(this.claim);
      const diaryLeave = numericAdaOrLeaveKeyFromClaim({
        fileNumber: diary?.leaveNumber ?? recordId,
      });
      return (
        claimLeave != null &&
        diaryLeave != null &&
        claimLeave === diaryLeave
      );
    }

    const displayInfo = this.getRecordSourceDisplayInfo(
      recordSource,
      diary,
      recordId,
    );
    const diaryFileNumber = displayInfo.fileNumber;
    const claimFileNumber = this.claim.fileNumber;

    if (diaryFileNumber && claimFileNumber) {
      return diaryFileNumber === claimFileNumber;
    }

    const diaryRecordId = recordId?.toString();
    const claimRecordId = this.claim.recordId?.toString();

    return (
      recordSource === this.claim.recordSource &&
      diaryRecordId === claimRecordId
    );
  }

  prepareCalendarDataSource() {
    const weekMap: any = {};
    for (let i = 0; i < 7; i++) {
      const date = new Date(this.weekStartDate);
      date.setDate(date.getDate() + i);
      const dayKey = WEEKDAYS[date.getDay()];
      weekMap[dayKey] = {
        day: dayKey,
        date: date.toISOString(),
        displayDate: DateEx.formatShortDate(date),
        completed: 0,
        pastDue: 0,
        draft: 0,
        open: 0,
      };
    }
    const today = new Date();
    const todayDateOnly = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
    );

    const startOfWeek = new Date(
      this.weekStartDate.getFullYear(),
      this.weekStartDate.getMonth(),
      this.weekStartDate.getDate(),
    );
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(endOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);

    const dataSource =
      this.weekDiaryData.length > 0 ? this.weekDiaryData : this.diaryData;

    for (const diary of dataSource) {
      const due = new Date(diary.dateDue);

      if (due < startOfWeek || due > endOfWeek) {
        continue;
      }

      if (!this.isPageTodo && this.claim) {
        const { recordSource, recordId } = this.getRecordSourceAndId(diary);

        if (!this.matchesCurrentClaim(diary, recordSource, recordId)) {
          continue;
        }
      }

      const dayKey = WEEKDAYS[due.getDay()];
      const item = weekMap[dayKey];
      const itemDate = item ? new Date(item.date) : null;

      if (
        !item ||
        itemDate.getFullYear() !== due.getFullYear() ||
        itemDate.getMonth() !== due.getMonth() ||
        itemDate.getDate() !== due.getDate()
      ) {
        continue;
      }

      const dueDateOnly = new Date(
        due.getFullYear(),
        due.getMonth(),
        due.getDate(),
      );

      const status = (diary.status || '').toLowerCase();

      if (dueDateOnly < todayDateOnly && status !== 'completed') {
        item.pastDue++;
      } else if (status === 'completed') {
        item.completed++;
      } else if (status === 'open') {
        item.open++;
      }
    }
    return Object.values(weekMap);
  }

  onDiaryDataReceived(data: any) {
    this.diaryData = data;
    if (!this.isClndrClicked) {
      this.weekDiaryData = data;
    }
    this.searchCount = data.length;

    if (this.tab.value === 'browse') {
      this.calendarItems = this.prepareCalendarDataSource();
    } else if (!this.isClndrClicked && this.isSelectedDateInWeek()) {
      this.calendarItems = this.prepareCalendarDataSource();
    } else if (this.initializingDiaries) {
      this.calendarItems = this.prepareCalendarDataSource();
    }

    this.isMarkCompleteEventFired = false;
    this.updateActiveTitle();

    this.tableLoading = false;
  }

  private isSelectedDateInWeek(): boolean {
    if (!this.selectedDate || !this.weekStartDate || !this.weekEndDate)
      return false;
    const sel = new Date(this.selectedDate);
    const start = new Date(this.weekStartDate);
    const end = new Date(this.weekEndDate);

    const selM = sel.getMonth(),
      selD = sel.getDate();
    const startM = start.getMonth(),
      startD = start.getDate();
    const endM = end.getMonth(),
      endD = end.getDate();

    const isAfterOrSameStart =
      selM > startM || (selM === startM && selD >= startD);
    const isBeforeOrSameEnd = selM < endM || (selM === endM && selD <= endD);

    return isAfterOrSameStart && isBeforeOrSameEnd;
  }

  private filterDiariesByDate(diaries: any[], targetDate: Date): any[] {
    return diaries.filter((diary: any) => {
      const dueDate = new Date(diary.dateDue);
      return dueDate.toDateString() === targetDate.toDateString();
    });
  }

  @HandleLoadingState({ errorProperty: 'actionError' })
  async getDiaryCount() {
    this.diaryCount = await this.api.clientDiary.getDiaryCount();
  }

  @HandleLoadingState({
    loadingProperty: 'state',
    onValue: 'Loading',
    offValue: 'Loaded',
  })
  @HandleLoadingState({ loadingProperty: 'tableLoading', useViewLoading: true })
  async getDiaryEntries() {
    this.diaryResponseData = await this.api.clientDiary.getDiaryEntries(
      this.diaryRequest,
    );
    this.onDiaryDataReceived(this.diaryResponseData);
  }

  private updateActiveTitle() {
    if (this.tab.value === 'search') {
      const count = this.searchCount ?? 0;
      this.activeTitle = `${count} Diar${count === 1 ? 'y' : 'ies'}`;
    } else {
      this.activeTitle = this.selectedDate ?? '';
    }
  }

  prepareRequest(
    weekStartDate: any | null = null,
    weekEndDate: any | null = null,
  ): SearchDiariesRequest {
    const request: SearchDiariesRequest = {
      diaryStatus: 0,
      dueDateStart: weekStartDate,
      dueDateEnd: weekEndDate,
      duePast: false,
    };

    if (this.isPageTodo) {
      request.followUpBy = this.session.user.userName;
    } else if (this.claim && this.claim.recordId != null) {
      const claimSpecificFields = this.getClaimSpecificFields();
      Object.assign(request, claimSpecificFields);
    }

    return request;
  }

  onCalendarAction(event: {
    action: string;
    weekStartDate: Date;
    weekEndDate: Date;
    claimSpecificFields?: any;
  }) {
    this.tableLoading = true;
    this.selectedDate = DateEx.formatShortDate(event.weekStartDate);
    this.weekStartDate = DateEx.getStartOfWeek(event.weekStartDate);
    this.weekEndDate = DateEx.getEndOfWeek(this.weekStartDate);
    this.diaryRequest = this.prepareRequest(
      event.weekStartDate,
      event.weekEndDate,
    );
    this.isClndrClicked = false;
    this.initializingDiaries = false;
    switch (event.action) {
      case 'calendarClick': {
        this.isClndrClicked = true;
        const clickedDate = new Date(event.weekStartDate);
        this.selectedDayData = this.filterDiariesByDate(
          this.weekDiaryData,
          clickedDate,
        );
        this.selectedDate = DateEx.formatShortDate(clickedDate);
        this.searchCount = this.selectedDayData.length;
        this.activeItemDateEvent(this.selectedDate);
        this.updateActiveTitle();
        this.tableLoading = false;
        break;
      }
      case 'previous':
      case 'next': {
        const dateStr = this.selectedDate;
        this.activeItemDateEvent(dateStr);
        this.getDiaryEntries();
        break;
      }
      case 'init': {
        this.initializingDiaries = true;
        this.getDiaryEntries();
        this.activeItemDateEvent(this.selectedDate);
        break;
      }
      case 'today': {
        this.activeItemDateEvent(DateEx.formatShortDate(new Date()));
        this.getDiaryEntries();
        break;
      }
      default:
        break;
    }
  }

  get filteredDiaryData() {
    const sourceData = this.isClndrClicked
      ? this.selectedDayData
      : this.diaryData;

    if (
      this._lastDiaryData === sourceData &&
      this._lastIncludeCompleted === this.includeCompleted &&
      this._lastIsPageTodo === this.isPageTodo
    ) {
      return this._lastFilteredDiaryTableData;
    }
    const diaries =
      this.includeCompleted || this.tab.value === 'search'
        ? sourceData
        : sourceData.filter(
            (diary: any) => (diary.status || '').toLowerCase() !== 'completed',
          );
    this._lastDiaryData = sourceData;
    this._lastIncludeCompleted = this.includeCompleted;
    this._lastIsPageTodo = this.isPageTodo;
    this._lastFilteredDiaryTableData = diaries
      .map((diary: any) => {
        let recordSource: string;
        let recordId: any;
        const apiRecordId = this.normalizeDiaryRecordId(diary);
        const tamsReq =
          diary?.adatamsRequestID ?? diary?.adaTamsRequestID ?? null;
        const ln = diary?.leaveNumber;

        switch (true) {
          case !!diary.claimUID:
            recordSource = 'J';
            recordId = diary.claimUID;
            break;
          case ln !== undefined && ln !== null && ln !== '':
            recordSource = 'L';
            recordId = ln;
            break;
          case !!diary.absenceUID:
            recordSource = 'T';
            recordId =
              diary.absenceUID ||
              diary.employeeUID ||
              apiRecordId ||
              this.claim?.recordId;
            break;
          case !!tamsReq:
            recordSource = 'R';
            recordId = tamsReq;
            break;
          case !!diary.adaEventNumber:
            recordSource = 'A';
            recordId = diary.adaEventNumber;
            break;
          case !!diary.employeeUID:
            recordSource = 'T';
            recordId =
              diary.employeeUID ||
              apiRecordId ||
              this.claim?.recordId;
            break;
          default:
            recordSource = '';
            recordId = null;
            break;
        }
        const displayInfo = this.getRecordSourceDisplayInfo(
          recordSource,
          diary,
          recordId,
        );
        const claimDisplayValue = displayInfo.claimDisplayValue;
        const fileNumber = displayInfo.fileNumber;

        return {
          id: diary.diaryUID,
          clientId: diary.clientNumber,
          case: claimDisplayValue,
          fileNumber: fileNumber,
          firstName: diary.firstName,
          lastName: diary.lastName,
          fullName:
            diary.lastName && diary.firstName
              ? `${diary.lastName}, ${diary.firstName}`
              : '',
          dueDate: diary.dateDue,
          notificationDate:
            diary.emailReminder && !diary.notificationTime
              ? '3:00 PM CT'
              : diary.notificationTime,
          formattedDueDate: this.formatPipe.transform(diary.dateDue, 'd'),
          formattedNotificationDate:
            diary.emailReminder && !diary.notificationTime
              ? '3:00 PM CT'
              : this.formatPipe.transform(diary.notificationTime, 't'),
          diaryType: diary.typeDescription,
          diaryTypeId: diary.typeUID,
          formattedUsers: diary.additionalUsers
            ? `${diary.followUpUser} ${diary.additionalUsers
                .map((a: any) => a.userName)
                .join(' ')}`
            : diary.followUpUser,
          followUpUser: diary.followUpUser,
          additionalUsers: diary.additionalUsers,
          status: this.getStatusLabel(diary.status, diary.dateDue),
          message: diary.description,
          allowOthers: !diary.completeByCreator,
          emailReminder: diary.emailReminder,
          isCompleted: diary.status === 'Completed',
          createdBy: diary.createdBy,
          recordSource,
          recordId,
          navigatingRecID: apiRecordId ?? recordId,
          format: diary?.format,
        };
      })
      .filter((mappedDiary: any) => {
        // Filter diaries to match current claim when not on page todo
        if (!this.isPageTodo && this.claim) {
          const originalDiary = diaries.find(
            (d: any) => d.diaryUID === mappedDiary.id,
          );
          if (originalDiary) {
            const { recordSource, recordId } =
              this.getRecordSourceAndId(originalDiary);

            return this.matchesCurrentClaim(
              originalDiary,
              recordSource,
              recordId,
            );
          }
          return false;
        }
        return true;
      });

    if (this.tab.value === 'search') {
      this.searchCount = this._lastFilteredDiaryTableData.length;
      this.updateActiveTitle();
    }

    return this._lastFilteredDiaryTableData;
  }

  get filteredCalendarItems() {
    if (
      this._lastCalendarItems === this.calendarItems &&
      this._lastIncludeCompletedCal === this.includeCompleted &&
      this._lastSelectedDate === this.selectedDate
    ) {
      return this._lastFilteredCalendarItems;
    }
    const items = this.calendarItems.map((item) => {
      if (!this.includeCompleted) {
        return { ...item, completed: 0 };
      }
      return item;
    });
    if (
      (this.isClndrClicked || this.isMarkCompleteEventFired) &&
      this.selectedDate &&
      Array.isArray(items)
    ) {
      for (const item of items) {
        item.active =
          DateEx.formatShortDate(new Date(item.date)) === this.selectedDate;
      }
    }
    this._lastCalendarItems = this.calendarItems;
    this._lastIncludeCompletedCal = this.includeCompleted;
    this._lastSelectedDate = this.selectedDate;
    this._lastFilteredCalendarItems = items;
    return items;
  }

  getStatusLabel(status: string, dueDate: Date): string {
    const now = new Date();
    const nowDateOnly = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    );
    const due = new Date(dueDate);
    const dueDateOnly = new Date(
      due.getFullYear(),
      due.getMonth(),
      due.getDate(),
    );
    const normalizedStatus = status?.toLocaleLowerCase();

    if (normalizedStatus === 'completed') return 'Completed';
    if (normalizedStatus === 'draft') return 'Draft';
    if (dueDateOnly < nowDateOnly && normalizedStatus !== 'completed')
      return 'Past due';
    return 'Open';
  }

  activeItemDateEvent(event: string) {
    this.selectedDate = event;
    this.activeTitle = this.selectedDate;
  }

  searchRequest(request: SearchDiariesRequest) {
    this.tableLoading = true;
    this.diaryRequest = request;
    if (!this.isPageTodo && this.claim && this.claim.recordId != null) {
      const claimSpecificFields = this.getClaimSpecificFields();
      Object.assign(this.diaryRequest, claimSpecificFields);
    }

    this.getDiaryEntries();
  }

  searchCountEvent(event: number) {
    this.searchCount = event;
  }

  @HandleLoadingState({
    loadingProperty: 'tableLoading',
    errorProperty: 'actionError',
  })
  async markCompleteEvent(event: number) {
    await this.api.clientDiary.markComplete(event);
    this.clientDiaryData.notifyChanged();
    this.diaryRequest = this.prepareRequest(
      this.weekStartDate,
      this.weekEndDate,
    );
    this.diaryResponseData = await this.api.clientDiary.getDiaryEntries(
      this.diaryRequest,
    );
    this.isClndrClicked = false;
    this.isMarkCompleteEventFired = true;
    this.onDiaryDataReceived(this.diaryResponseData);

    if (this.selectedDate) {
      const selectedDate = new Date(this.selectedDate);
      this.selectedDayData = this.filterDiariesByDate(
        this.weekDiaryData,
        selectedDate,
      );
    }
  }

  onIncludeCompletedChange(value: boolean) {
    this.includeCompleted = value;
  }

  onClearSearch() {
    this.diaryData = [];
    this.searchCount = 0;
    this.activeTitle = '0 Diaries';
    this.tableLoading = false;
  }

  onSelectRadio(event: any) {
    if (event.target.defaultValue === 'search') {
      this.diaryData = [];
      this.searchCount = 0;
      this.activeTitle = '0 Diaries';
      this.tableLoading = false;
      this.isClndrClicked = false;
    } else {
      this.activeTitle =
        this.selectedDate ?? DateEx.formatShortDate(new Date());

      this.tableLoading = true;
      this.isClndrClicked = false;

      const currentDate = this.weekStartDate || new Date();
      const weekStart = DateEx.getStartOfWeek(currentDate);
      const weekEnd = DateEx.getEndOfWeek(weekStart);
      this.weekStartDate = weekStart;
      this.weekEndDate = weekEnd;
      this.selectedDate = DateEx.formatShortDate(weekStart);
      this.diaryRequest = this.prepareRequest(weekStart, weekEnd);

      this.getDiaryEntries();
    }
  }

  private getRecordSourceDisplayInfo(
    recordSource: string,
    diary: any,
    recordId: any,
  ): { claimDisplayValue: string; fileNumber: string } {
    switch (recordSource) {
      case 'J':
        return {
          claimDisplayValue: diary.claimUID || '',
          fileNumber: diary.diaryFor || '',
        };
      case 'T':
        return {
          claimDisplayValue: recordId,
          fileNumber: diary.absenceUID || diary.employeeUID || '',
        };
      case 'A':
        return {
          claimDisplayValue: diary.adaEventNumber || '',
          fileNumber: diary.adaEventNumber || '',
        };
      case 'L':
        return {
          claimDisplayValue: diary.leaveNumber || '',
          fileNumber: diary.leaveNumber || '',
        };
      case 'R':
        return {
          claimDisplayValue: diary.adatamsEventNumber || '',
          fileNumber: diary.adatamsEventNumber || '',
        };
      default:
        return {
          claimDisplayValue: '',
          fileNumber: diary.diaryFor || '',
        };
    }
  }

  private getClaimSpecificFields(): any {
    const z = {
      claimUID: null as any,
      absenceUID: null as any,
      aDAEventNumber: null as any,
      leaveNumber: null as any,
      employeeUID: null as any,
    };
    const claim = this.view.currentClaim();
    if (!claim) {
      return {
        claimUID: null,
        absenceUID: null,
        aDAEventNumber: null,
        leaveNumber: null,
      };
    }
    switch (claim.recordSource) {
      case 'J':
        return { ...z, claimUID: claim.recordId };
      case 'T':
        return {
          ...z,
          absenceUID: claim.fileNumber,
          employeeUID: claim.hrMasterUid,
        };
      case 'A':
        return {
          ...z,
          aDAEventNumber: numericAdaOrLeaveKeyFromClaim(claim),
        };
      case 'L':
        return { ...z, leaveNumber: numericAdaOrLeaveKeyFromClaim(claim) };
      case 'R':
        return { ...z };
      default:
        return { ...z, claimUID: claim.recordId };
    }
  }
}
